import { Box, Divider, Skeleton, useToast } from "@chakra-ui/react";
import {
  Dot,
  DropdownFilter,
  NoDataPlaceholder,
  QuickActions,
  Table,
  TablePageSkeleton,
  CreatedByFilter,
} from "components/ui";
import {
  useGetAllAppraisalsQuery,
  useDeleteAppraisalMutation,
  Money,
  AppraisalNode,
  useExportAppraisalPdfMutation,
} from "graphql/queries/generated/queries";
import getColumns from "helpers/getColumns";
import { Trans, useTranslation } from "react-i18next";
import { RiAddLine } from "react-icons/ri";
import { createdByFilterFunc } from "components/ui/CreatedByFilter";
import { handleMutation } from "middleware/Toaster";
import parseAmount from "helpers/parseAmount";
import downloadFile from "helpers/downloadFile";

const QUICK_ACTIONS = [
  {
    name: "Add new Appraisal entry",
    href: "new",
    icon: <RiAddLine />,
  },
];

const CUSTOM_FIELDS = [
  "appraisalStatus",
  "appraisalDate",
  "appraisalEvaluator",
  "appraisalAsset",
  "appraisalAssetAuthor",
  "mediaNumber",
  "createdAt",
  "actualMediaStatus",
  "appraisalAsset.actualDateAndPeriod",
  "valueEstimatedValue",
  "createdBy",
];
const CUSTOM_COLUMNS = [
  {
    Header: "Id",
    Footer: "Id",
    accessor: "id",
    disableFilters: true,
    Cell: ({ cell: { value } }: { cell: { value: string } }) => <div>{value}</div>,
  },
  {
    Header: "Appraisal Date",
    Footer: "Appraisal Date",
    accessor: "appraisalDate",
    disableFilters: true,
    Cell: ({ cell: { value } }: { cell: { value: string } }) => {
      if (value) {
        const d = new Date(value).toLocaleDateString("it-IT");
        return <div>{d}</div>;
      }
      return <div />;
    },
  },
  {
    Header: "Evaluator",
    Footer: "Evaluator",
    accessor: "appraisalEvaluator",
    disableFilters: true,
    Cell: ({ cell: { value } }: { cell: { value: { id: string; fullName: string } } }) => {
      if (value !== undefined && value !== null && value.fullName !== null)
        return <div>{value.fullName}</div>;
      return <div />;
    },
  },
  {
    Header: "Value",
    Footer: "Value",
    accessor: "valueEstimatedValue",
    Cell: ({ cell: { value } }: { cell: { value: Money } }) => parseAmount(value),
    weight: 0,
    disableFilters: true,
  },

  {
    Header: "Object Author",
    Footer: "Object Author",
    accessor: "appraisalAsset.authorEntity",
    disableFilters: true,
    Cell: ({ cell: { value } }: { cell: { value: { id: string; fullName: string } } }) => {
      if (value !== undefined && value !== null && value.fullName !== null)
        return <div>{value.fullName}</div>;
      return <div />;
    },
  },
  {
    Header: "Object Title",
    Footer: "Object Title",
    accessor: "appraisalAsset",
    disableFilters: true,
    Cell: ({ cell: { value } }: { cell: { value: { id: string; title: string } } }) => {
      if (value !== undefined && value !== null && value.title !== null)
        return <div>{value.title}</div>;
      return <div />;
    },
  },

  {
    Header: "Created On",
    Footer: "Created On",
    accessor: "appraisalAsset.actualDateAndPeriod",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 0,
  },

  {
    Header: "Documents N.",
    Footer: "Documents N.",
    accessor: "mediaNumber",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => value,
    disableFilters: true,
    weight: 0,
  },
  {
    Header: "STATUS",
    Footer: "STATUS",
    accessor: "appraisalStatus",
    Filter: DropdownFilter,
    Cell: ({ cell: { value } }: { cell: { value: string } }) => (
      <Dot positiveLabel="Draft" negativeLabel="Closed" status={value === "draft"} />
    ),
  },
  {
    Header: "",
    accessor: "actualMediaStatus",
    Filter: DropdownFilter,
    Cell: ({ cell: { value } }: { cell: { value: string } }) => <span />,
    weight: 0,
  },
  {
    Header: "",
    accessor: "createdBy.name",
    Cell: ({ cell: { value } }: { cell: { value: string } }) => <span />,
    weight: 0,
    Filter: CreatedByFilter,
    filter: createdByFilterFunc,
  },
];
const FILTERABLE_COLUMNS = ["deleted", "type"];

const AppraisalPage = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useGetAllAppraisalsQuery();
  const [deleteAppraisal, { data: deleteData, error: deleteError, loading: deleteLoading }] =
    useDeleteAppraisalMutation({
      ...handleMutation("Appraisal deleted!"),
    });

  const [
    exportAppraisalPdf,
    {
      data: exportAppraisalPdfData,
      error: exportAppraisalPdfError,
      loading: exportAppraisalPdfLoading,
    },
  ] = useExportAppraisalPdfMutation({ ...handleMutation("Appraisal exported!") });

  if (loading) return <TablePageSkeleton loading={loading} />;

  if (!data || !data.allAppraisals || data.allAppraisals.edges.length === 0)
    return (
      <Box>
        <QuickActions actions={QUICK_ACTIONS} />
        <Divider borderColor="gray.100" />
        <NoDataPlaceholder />
      </Box>
    );

  const {
    allAppraisals: { edges },
  } = data;

  if (edges[0] === null || !edges[0].node) return null;

  const dataToGetColumns = edges[0].node;

  const columns = getColumns({
    data: dataToGetColumns,
    customFields: CUSTOM_FIELDS,
    customColumns: CUSTOM_COLUMNS,
    filterableColumns: FILTERABLE_COLUMNS,
  });

  const onDelete = async (id: string) => {
    await deleteAppraisal({
      variables: {
        input: {
          id,
        },
      },
    });

    await refetch();
  };

  const onGeneratePdf = async (appraisalId: string) => {
    const { data } = await exportAppraisalPdf({
      variables: {
        input: {
          appraisalId,
        },
      },
    });

    const {
      exportAppraisalPdf: { url },
    } = data;

    await downloadFile(url);
  };

  const BATCH_ACTIONS = [
    {
      name: <Trans>Delete exhibition(s)</Trans>,
      intent: "danger",
      action: async (selectedRows: { original: AppraisalNode }[]) => {
        await Promise.all(
          selectedRows.map(async (row) => {
            const { original } = row;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            await onDelete(original.id);
            return null;
          })
        );
        await refetch();
      },
    },
    {
      name: <Trans>Export PDF</Trans>,
      action: async (selectedRows: { original: AppraisalNode }[]) => {
        await Promise.all(
          selectedRows.map(async (row) => {
            const { original } = row;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            await onGeneratePdf(original.id);
            return null;
          })
        );
      },
      loading: exportAppraisalPdfLoading,
    },
  ];

  return (
    <Box>
      <QuickActions actions={QUICK_ACTIONS} />
      <Divider borderColor="gray.100" />
      <Skeleton isLoaded={!loading}>
        <Table
          batchActions={BATCH_ACTIONS}
          columns={columns}
          data={data.allAppraisals.edges.flatMap((edge) => edge?.node)}
          pathname="/mgmt/appraisal/"
          deleteAction={(id: string) => onDelete(id)}
        />
      </Skeleton>
    </Box>
  );
};

export default AppraisalPage;
